import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { RefObject } from 'preact';
import { api, type WorkQuery } from '../api/client';
import type { WorkSummary } from '../api/types';

export type InfiniteStatus = 'idle' | 'loading-first' | 'loading-more' | 'ready' | 'error';

export interface InfiniteWorks {
    items: WorkSummary[];
    totalCount: number | null;
    totalPages: number | null;
    page: number;
    pageSize: number;
    status: InfiniteStatus;
    error: Error | null;
    hasMore: boolean;
    loadMore: () => void;
    sentinelRef: RefObject<HTMLElement>;
}

/**
 * Progressive loader over <c>GET /api/works</c>. The catalog table /
 * grid mounts this hook once and renders a sentinel element somewhere
 * near the bottom of the visible page; the hook's IntersectionObserver
 * fires the next page fetch as soon as the sentinel comes into view.
 *
 * <para>Returns the accumulated <c>items</c> array (every page so far
 * concatenated) plus the standard Koan pagination signals so the caller
 * can show "X of Y" counters without a separate request.</para>
 *
 * <para>Reset semantics: when <c>apiSet</c> or query inputs change the
 * accumulator clears and we re-fetch from page 1. The locale switcher
 * relies on this - flipping locale should swap the catalog wholesale,
 * not append to the existing set.</para>
 */
export function useInfiniteWorks(
    query: WorkQuery = {},
    apiSet?: string,
    pageSize = 100,
): InfiniteWorks {
    const [items, setItems] = useState<WorkSummary[]>([]);
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState<number | null>(null);
    const [totalPages, setTotalPages] = useState<number | null>(null);
    const [status, setStatus] = useState<InfiniteStatus>('idle');
    const [error, setError] = useState<Error | null>(null);

    const sentinelRef = useRef<HTMLElement>(null);
    // Cancellation token so a stale fetch (locale flipped mid-flight) can't
    // append its rows after the reset.
    const generationRef = useRef(0);
    // Live page counter the IntersectionObserver closure reads - avoids
    // the stale-closure trap where the observer captures page=0 on mount
    // and never sees subsequent updates.
    const pageRef = useRef(0);
    const totalPagesRef = useRef<number | null>(null);
    const inFlightRef = useRef(false);
    // Aborts the in-flight request when a newer one supersedes it (locale
    // flip, filter change) so the server can drop the superseded query
    // instead of running it to completion for nothing.
    const abortRef = useRef<AbortController | null>(null);

    const queryKey = JSON.stringify(query);

    const fetchPage = useCallback(
        async (next: number) => {
            if (inFlightRef.current) return;
            const gen = generationRef.current;
            inFlightRef.current = true;
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            setStatus(next === 1 ? 'loading-first' : 'loading-more');
            try {
                const result = await api.works.list(
                    { ...query, page: next, pageSize },
                    apiSet,
                    controller.signal,
                );
                if (gen !== generationRef.current) return;
                setItems((prev) => (next === 1 ? result.items : [...prev, ...result.items]));
                setPage(result.page);
                pageRef.current = result.page;
                setTotalCount(result.totalCount);
                setTotalPages(result.totalPages);
                totalPagesRef.current = result.totalPages;
                setStatus('ready');
                setError(null);
            } catch (e) {
                if (gen !== generationRef.current) return;
                if (e instanceof DOMException && e.name === 'AbortError') return;
                setStatus('error');
                setError(e instanceof Error ? e : new Error(String(e)));
            } finally {
                inFlightRef.current = false;
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [queryKey, apiSet, pageSize],
    );

    // Reset + initial load when the locale or query changes.
    useEffect(() => {
        generationRef.current += 1;
        pageRef.current = 0;
        totalPagesRef.current = null;
        inFlightRef.current = false;
        abortRef.current?.abort();
        setItems([]);
        setPage(0);
        setTotalCount(null);
        setTotalPages(null);
        setError(null);
        void fetchPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryKey, apiSet, pageSize]);

    const loadMore = useCallback(() => {
        const current = pageRef.current;
        const total = totalPagesRef.current;
        if (inFlightRef.current) return;
        if (total !== null && current >= total) return;
        void fetchPage(current + 1);
    }, [fetchPage]);

    // Sentinel observer. Re-attaches whenever the ref node mounts.
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node) return;
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        loadMore();
                    }
                }
            },
            { rootMargin: '400px 0px' },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [loadMore, items.length]);

    const hasMore =
        totalPages === null ? false : page < totalPages;

    return {
        items,
        totalCount,
        totalPages,
        page,
        pageSize,
        status,
        error,
        hasMore,
        loadMore,
        sentinelRef,
    };
}
